import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../context/MemberContext';
import apiClient from '../api/client';
import './Page.css';
import './HomePage.css';

interface NextClass {
  calendarInstanceId: number;
  calendarName: string;
  calendarColor: string;
  startDate: string;
  endDate: string;
}

interface DashboardData {
  packageName: string;
  membershipStatus: string;
  nextBillDate: string | null;
  billingAmount: number | null;
  lastCheckIn: string | null;
  checkInsThisWeek: number;
  hasPastDueBalance: boolean;
  pastDueAmount: number | null;
  nextClass: NextClass | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtLastVisit(iso: string | null): string {
  if (!iso) return 'No visits recorded';
  const d = new Date(iso);
  const today = new Date();
  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return fmtDate(iso);
}

export default function HomePage() {
  const { member } = useMember();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    if (!member?.personId) return;
    apiClient
      .get(`/PwaDashboard/${member.personId}`)
      .then(res => setData(res.data?.data ?? null))
      .catch(() => {});
  }, [member?.personId]);

  return (
    <div className="page">
      <div className="page-content home-content">
        <p className="home-greeting">Hello, {member?.firstName ?? ''}!</p>

        {data?.hasPastDueBalance && (
          <button
            type="button"
            className="past-due-banner"
            onClick={() => navigate('/settings', { state: { openSheet: 'payment' } })}
          >
            <span className="past-due-banner__title">Pay Past Due</span>
            {data.pastDueAmount != null && (
              <span className="past-due-banner__amount">${data.pastDueAmount.toFixed(2)} due</span>
            )}
            <span className="past-due-banner__btn">Pay Past Due</span>
          </button>
        )}

        {member?.isActive === false && (
          <div className="home-inactive-banner">
            <p className="home-inactive-banner__status">
              Status: <strong>{data?.membershipStatus ?? 'Inactive'}</strong>
            </p>
            <p className="home-inactive-banner__msg">
              If your status is not active, click <strong>Signup</strong> in the menu below and sign up for a membership to activate your account.
            </p>
          </div>
        )}

        {/* Next Class card */}
        <button
          type="button"
          className="dash-card dash-card--clickable"
          onClick={() => navigate('/schedule')}
        >
          <div className="dash-card__label">Next Class</div>
          {data?.nextClass ? (
            <>
              <div
                className="dash-card__accent"
                style={{ background: data.nextClass.calendarColor || 'var(--accent, #e94560)' }}
              />
              <div className="dash-card__main">{data.nextClass.calendarName}</div>
              <div className="dash-card__sub">
                {fmtDay(data.nextClass.startDate)} · {fmtTime(data.nextClass.startDate)}
              </div>
            </>
          ) : (
            <div className="dash-card__sub">No upcoming classes booked</div>
          )}
          <span className="dash-card__chevron">›</span>
        </button>

        {/* My Plan card */}
        <button
          type="button"
          className="dash-card dash-card--clickable"
          onClick={() => navigate('/settings')}
        >
          <div className="dash-card__label">My Plan</div>
          {data ? (
            <>
              <div className="dash-card__main">{data.packageName || '—'}</div>
              <div className="dash-card__sub">
                {data.membershipStatus}
                {data.nextBillDate ? ` · Renews ${fmtDate(data.nextBillDate)}` : ''}
                {data.billingAmount != null ? ` · $${data.billingAmount.toFixed(2)}/mo` : ''}
              </div>
            </>
          ) : (
            <div className="dash-card__sub">Loading…</div>
          )}
          <span className="dash-card__chevron">›</span>
        </button>

        {/* Last Visit card */}
        <div className="dash-card">
          <div className="dash-card__label">Last Visit</div>
          <div className="dash-card__main">{fmtLastVisit(data?.lastCheckIn ?? null)}</div>
          <div className="dash-card__sub">
            {(data?.checkInsThisWeek ?? 0) === 0
              ? 'No check-ins this week'
              : `${data!.checkInsThisWeek} check-in${data!.checkInsThisWeek !== 1 ? 's' : ''} this week`}
          </div>
        </div>
      </div>
    </div>
  );
}
